<script setup lang="ts">
import { computed, onBeforeUnmount, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useEventStore } from '@/stores/event-store.js'
import MainNavAttachmentMenu from '@/components/MainNavAttachmentMenu.vue'
import { useEventFiles } from '@/stores/event-files-composable'
import { useAuthClient } from '@/stores/auth-store.ts'
import { IconX } from '@tabler/icons-vue'

const eventStore = useEventStore()
const route = useRoute()
const { handleImageFilesUpload } = useEventFiles()
const { post } = useAuthClient()

const inputTextEl = ref<HTMLDivElement | null>(null)
const inputText = ref('')
const isRecording = ref(false)
const isTranscribing = ref(false)
const isSubmittingComposer = ref(false)
const mediaRecorder = ref<MediaRecorder | null>(null)
const currentStream = ref<MediaStream | null>(null)
const recordedChunks = ref<Blob[]>([])
const shouldTranscribeOnStop = ref(true)
const composerImages = ref<Array<{ id: string; file: File; previewUrl: string }>>([])

const hasComposerText = computed(() => inputText.value.trim().length > 0)
const hasComposerImages = computed(() => composerImages.value.length > 0)
const canSubmitComposer = computed(() => hasComposerText.value || hasComposerImages.value)
const showQuickCreate = computed(() => !eventStore.showEvent && route.name !== 'event')

function updateData() {
  inputText.value = inputTextEl.value?.innerText ?? ''
}

function clearComposerText() {
  if (inputTextEl.value) {
    inputTextEl.value.innerText = ''
  }
  inputText.value = ''
}

function clearComposerImages() {
  for (const image of composerImages.value) {
    URL.revokeObjectURL(image.previewUrl)
  }
  composerImages.value = []
}

function clearComposerState() {
  clearComposerText()
  clearComposerImages()
}

function focusComposer() {
  inputTextEl.value?.focus()
}

function queueComposerImages(files: File[]) {
  if (!files.length) return

  const nextImages = files
    .filter((file) => file.type.startsWith('image/'))
    .map((file, index) => ({
      id: `${Date.now()}-${index}-${Math.random().toString(16).slice(2)}`,
      file,
      previewUrl: URL.createObjectURL(file),
    }))

  if (!nextImages.length) return
  composerImages.value = [...composerImages.value, ...nextImages]
  focusComposer()
}

function removeComposerImage(imageId: string) {
  const image = composerImages.value.find((item) => item.id === imageId)
  if (!image) return

  URL.revokeObjectURL(image.previewUrl)
  composerImages.value = composerImages.value.filter((item) => item.id !== imageId)
}

async function submitComposer() {
  if (isSubmittingComposer.value || !canSubmitComposer.value) return

  const text = inputText.value.trim()
  const imageFiles = composerImages.value.map((image) => image.file)

  isSubmittingComposer.value = true
  try {
    if (imageFiles.length === 0) {
      await eventStore.createEventFromText(text)
      clearComposerState()
      return
    }

    if (text) {
      await eventStore.createEventFromText(text)
      if (!eventStore.currentEvent?.id) {
        return
      }

      clearComposerState()
      await handleImageFilesUpload(imageFiles)
      return
    }

    const createdEvent = await eventStore.createQuickEvent({
      title: imageFiles.length > 1 ? `Photo upload (${imageFiles.length})` : 'Photo upload',
      description: text || 'Created from attachment menu',
      text: text || null,
    })

    if (!createdEvent?.id) {
      return
    }

    clearComposerState()
    await handleImageFilesUpload(imageFiles)
  } finally {
    isSubmittingComposer.value = false
  }
}

const activeNavIndex = computed(() => {
  const path = route.path

  if (path.startsWith('/ideas')) return 0
  if (path.startsWith('/activity')) return 1
  if (path === '/') return 2
  if (path.startsWith('/people')) return 3
  if (path.startsWith('/more')) return 4

  return 2
})

function isNavActive(navPath: string) {
  const path = route.path

  if (navPath === '/') return path === '/'
  return path.startsWith(navPath)
}

function setComposerText(text: string) {
  const nextText = text.trim()
  inputText.value = nextText
  if (inputTextEl.value) {
    inputTextEl.value.innerText = nextText
    focusComposer()
  }
}

async function startRecording() {
  if (!navigator.mediaDevices?.getUserMedia) {
    console.error('Audio recording is not supported in this browser.')
    return
  }

  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    currentStream.value = stream
    recordedChunks.value = []

    const preferredMimeType = 'audio/webm;codecs=opus'
    const supportsPreferredMimeType =
      typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(preferredMimeType)
    const recorder = supportsPreferredMimeType
      ? new MediaRecorder(stream, { mimeType: preferredMimeType })
      : new MediaRecorder(stream)

    recorder.ondataavailable = (event: BlobEvent) => {
      if (event.data.size > 0) {
        recordedChunks.value.push(event.data)
      }
    }

    recorder.onstop = () => {
      if (shouldTranscribeOnStop.value) {
        void transcribeRecording()
      } else {
        const streamToStop = currentStream.value
        currentStream.value = null
        if (streamToStop) {
          streamToStop.getTracks().forEach((track) => track.stop())
        }
        mediaRecorder.value = null
        recordedChunks.value = []
        shouldTranscribeOnStop.value = true
      }
    }

    mediaRecorder.value = recorder
    recorder.start()
    isRecording.value = true
  } catch (error) {
    console.error('Failed to start microphone recording:', error)
  }
}

function stopRecording() {
  if (!mediaRecorder.value || mediaRecorder.value.state === 'inactive') return
  shouldTranscribeOnStop.value = true
  mediaRecorder.value.stop()
  isRecording.value = false
}

function cancelRecording() {
  if (!mediaRecorder.value || mediaRecorder.value.state === 'inactive') return
  shouldTranscribeOnStop.value = false
  mediaRecorder.value.stop()
  isRecording.value = false
}

function confirmRecording() {
  stopRecording()
}

async function transcribeRecording() {
  const chunks = recordedChunks.value
  const stream = currentStream.value

  currentStream.value = null
  if (stream) {
    stream.getTracks().forEach((track) => track.stop())
  }

  if (!chunks.length) {
    mediaRecorder.value = null
    return
  }

  isTranscribing.value = true

  try {
    const mimeType = mediaRecorder.value?.mimeType || 'audio/webm'
    const audioBlob = new Blob(chunks, { type: mimeType })
    const extension = mimeType.includes('mp4') || mimeType.includes('m4a') ? 'm4a' : 'webm'
    const audioFile = new File([audioBlob], `recording.${extension}`, { type: mimeType })

    const formData = new FormData()
    formData.append('audio', audioFile)

    const response = await post('/api/transcribe-audio', formData)
    if (!response.ok) {
      console.error(`Transcription failed with status ${response.status}`)
      return
    }

    const payload = (await response.json()) as { text?: string }
    if (payload.text?.trim()) {
      setComposerText(payload.text)
    }
  } catch (error) {
    console.error('Failed to transcribe recording:', error)
  } finally {
    isTranscribing.value = false
    mediaRecorder.value = null
    recordedChunks.value = []
    shouldTranscribeOnStop.value = true
  }
}

async function onComposerAction() {
  if (canSubmitComposer.value) {
    await submitComposer()
    return
  }

  if (isTranscribing.value || isSubmittingComposer.value) return

  if (isRecording.value) {
    stopRecording()
    return
  }

  await startRecording()
}

onBeforeUnmount(() => {
  if (mediaRecorder.value && mediaRecorder.value.state !== 'inactive') {
    mediaRecorder.value.stop()
  }
  if (currentStream.value) {
    currentStream.value.getTracks().forEach((track) => track.stop())
    currentStream.value = null
  }
  clearComposerImages()
})
</script>
<template>
  <Transition name="quick-create-slide">
    <section v-if="showQuickCreate" id="quick-create" class="relative">
      <div class="relative z-40 flex items-end gap-2 px-2 pt-2 pb-1">
        <MainNavAttachmentMenu
          class="self-end"
          @manual="focusComposer"
          @images-selected="queueComposerImages"
        />
        <div class="relative w-full min-w-0">
          <div
            v-if="hasComposerImages"
            class="mb-2 flex w-full max-w-full items-center gap-2 overflow-x-auto no-scrollbar pb-1"
          >
            <div
              v-for="image in composerImages"
              :key="image.id"
              class="relative h-16 w-16 flex-shrink-0 overflow-hidden rounded-xl border border-base-content/10 bg-base-200"
            >
              <img
                :src="image.previewUrl"
                alt="Selected image preview"
                class="h-full w-full object-cover"
              />
              <button
                type="button"
                class="absolute right-1 top-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-base-100/95 text-base-content ring-1 ring-base-content/20"
                aria-label="Remove image"
                @click.stop="removeComposerImage(image.id)"
              >
                <IconX :size="12" :stroke-width="2.5" />
              </button>
            </div>
          </div>
          <div class="relative">
            <div
              ref="inputTextEl"
              contentEditable="true"
              translate="no"
              data-virtualkeyboard="true"
              @input="updateData"
              class="composer-input min-h-10 w-full rounded-3xl border border-transparent dark:border-base-content/20 ring-1 ring-base-content/10 text-base-content bg-stone-100 dark:bg-base-100 py-2 pl-4 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 max-h-64 overflow-y-scroll caret-brand-500"
              :class="[
                isRecording ? 'pr-24' : 'pr-10',
                { 'composer-empty': !hasComposerText && !isRecording && !isTranscribing },
              ]"
              @keyup.enter.prevent="onComposerAction"
              placeholder="Type, speak, or add file to create event..."
            />
            <div
              v-if="isRecording"
              class="pointer-events-none absolute left-4 top-1/2 flex -translate-y-1/2 items-end gap-1"
              aria-hidden="true"
            >
              <span class="recording-bar"></span>
              <span class="recording-bar recording-bar-delay-1"></span>
              <span class="recording-bar recording-bar-delay-2"></span>
              <span class="recording-bar recording-bar-delay-3"></span>
            </div>
            <div
              v-else-if="isTranscribing"
              class="pointer-events-none absolute left-4 top-1/2 flex -translate-y-1/2 items-center gap-2 text-primary"
              aria-hidden="true"
            >
              <span class="transcribing-spinner"></span>
              <span class="text-xs font-medium">Processing...</span>
            </div>
            <div class="absolute right-2 top-1/2 flex -translate-y-1/2 items-center gap-1">
              <template v-if="isRecording">
                <button
                  class="flex h-8 w-8 items-center justify-center rounded-full bg-base-100 text-base-content ring-1 ring-base-content/20 hover:bg-base-200"
                  @click="cancelRecording"
                  aria-label="Cancel recording"
                  type="button"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path d="M18 6l-12 12" />
                    <path d="M6 6l12 12" />
                  </svg>
                </button>
                <button
                  class="flex h-8 w-8 items-center justify-center rounded-full bg-brand-600 text-white hover:bg-brand-500"
                  @click="confirmRecording"
                  aria-label="Transcribe recording"
                  type="button"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path d="M5 12l5 5l10 -10" />
                  </svg>
                </button>
              </template>
              <button
                v-else
                class="text-primary hover:text-blue-500 disabled:cursor-not-allowed disabled:opacity-70"
                :disabled="isTranscribing || isSubmittingComposer"
                @click="onComposerAction"
                type="button"
              >
                <span v-if="!canSubmitComposer" class="text-base-content">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="icon icon-tabler icons-tabler-outline icon-tabler-microphone"
                  >
                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                    <path
                      d="M9 2m0 3a3 3 0 0 1 3 -3h0a3 3 0 0 1 3 3v5a3 3 0 0 1 -3 3h0a3 3 0 0 1 -3 -3z"
                    />
                    <path d="M5 10a7 7 0 0 0 14 0" />
                    <path d="M8 21l8 0" />
                    <path d="M12 17l0 4" />
                  </svg>
                </span>

                <span v-else class="text-primary">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    class="icon icon-tabler icons-tabler-filled icon-tabler-circle-arrow-up"
                  >
                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                    <path
                      d="M17 3.34a10 10 0 1 1 -14.995 8.984l-.005 -.324l.005 -.324a10 10 0 0 1 14.995 -8.336zm-4.98 3.66l-.163 .01l-.086 .016l-.142 .045l-.113 .054l-.07 .043l-.095 .071l-.058 .054l-4 4l-.083 .094a1 1 0 0 0 1.497 1.32l2.293 -2.293v5.586l.007 .117a1 1 0 0 0 1.993 -.117v-5.585l2.293 2.292l.094 .083a1 1 0 0 0 1.32 -1.497l-4 -4l-.082 -.073l-.089 -.064l-.113 -.062l-.081 -.034l-.113 -.034l-.112 -.02l-.098 -.006z"
                    />
                  </svg>
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  </Transition>
  <nav
    class="px-2 z-20 h-[calc(3.5rem+env(safe-area-inset-bottom))] pb-[env(safe-area-inset-bottom)]"
  >
    <div class="relative flex h-full w-full">
      <div
        class="absolute bottom-0 left-0 w-1/5 flex justify-center pointer-events-none transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]"
        :style="{ transform: `translateX(${activeNavIndex * 100}%)` }"
      >
        <span class="h-1 w-7 rounded-full bg-brand-500" />
      </div>
      <router-link
        to="/ideas"
        class="relative flex-1 flex items-end justify-center pb-3"
        :class="isNavActive('/ideas') ? 'text-primary' : 'text-base-content'"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="icon icon-tabler icons-tabler-outline icon-tabler-sparkles-2 w-7 h-7"
        >
          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
          <path
            d="M13 7a9.3 9.3 0 0 0 1.516 -.546c.911 -.438 1.494 -1.015 1.937 -1.932c.207 -.428 .382 -.928 .547 -1.522c.165 .595 .34 1.095 .547 1.521c.443 .918 1.026 1.495 1.937 1.933c.426 .205 .925 .38 1.516 .546a9.3 9.3 0 0 0 -1.516 .547c-.911 .438 -1.494 1.015 -1.937 1.932a9 9 0 0 0 -.547 1.521c-.165 -.594 -.34 -1.095 -.547 -1.521c-.443 -.918 -1.026 -1.494 -1.937 -1.932a9 9 0 0 0 -1.516 -.547"
          />
          <path
            d="M3 14a21 21 0 0 0 1.652 -.532c2.542 -.953 3.853 -2.238 4.816 -4.806a20 20 0 0 0 .532 -1.662a20 20 0 0 0 .532 1.662c.963 2.567 2.275 3.853 4.816 4.806q .75 .28 1.652 .532a21 21 0 0 0 -1.652 .532c-2.542 .953 -3.854 2.238 -4.816 4.806a20 20 0 0 0 -.532 1.662a20 20 0 0 0 -.532 -1.662c-.963 -2.568 -2.275 -3.853 -4.816 -4.806a21 21 0 0 0 -1.652 -.532"
          />
        </svg>
      </router-link>
      <router-link
        to="/activity"
        class="relative flex-1 flex items-end justify-center pb-3"
        :class="isNavActive('/activity') ? 'text-primary' : 'text-base-content'"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="icon icon-tabler icons-tabler-outline icon-tabler-bell w-7 h-7"
        >
          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
          <path
            d="M10 5a2 2 0 1 1 4 0a7 7 0 0 1 4 6v3a4 4 0 0 0 2 3h-16a4 4 0 0 0 2 -3v-3a7 7 0 0 1 4 -6"
          />
          <path d="M9 17v1a3 3 0 0 0 6 0v-1" />
        </svg>
      </router-link>
      <router-link
        to="/"
        class="relative flex-1 flex items-end justify-center pb-3"
        :class="isNavActive('/') ? 'text-brand-500' : 'text-base-content'"
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          class="w-9 h-9"
          id="timeline-icon"
        >
          <g clip-path="url(#clip0_845_2943)">
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M24 12C24 18.6228 18.6228 24 12 24C5.3772 24 0 18.6228 0 12C0 5.3772 5.3772 0 12 0C18.6228 0 24 5.3772 24 12ZM7.176 12.501H10.4526C10.6578 13.119 11.2284 13.5612 11.9004 13.5612C12.5724 13.5612 13.1436 13.119 13.3482 12.501H15.7572C15.9828 13.5852 16.9254 14.4 18.0528 14.4C19.3482 14.4 20.4 13.3242 20.4 11.9988C20.4 10.6734 19.3482 9.6 18.0528 9.6C16.9254 9.6 15.9828 10.4124 15.7572 11.4966H13.3482C13.1436 10.881 12.5724 10.437 11.9004 10.437C11.2284 10.437 10.6578 10.881 10.4526 11.4966H7.176C6.9714 10.881 6.4002 10.437 5.7282 10.437C4.8846 10.437 4.2 11.1378 4.2 11.9988C4.2 12.8628 4.8846 13.5612 5.7282 13.5612C6.4002 13.5612 6.9714 13.119 7.176 12.501Z"
              fill="currentColor"
            />
          </g>
          <defs>
            <clipPath id="clip0_845_2943">
              <rect width="24" height="24" />
            </clipPath>
          </defs>
        </svg>
      </router-link>
      <router-link
        to="/people"
        class="relative flex-1 flex items-end justify-center pb-3"
        :class="isNavActive('/people') ? 'text-primary' : 'text-base-content'"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="icon icon-tabler icons-tabler-outline icon-tabler-users w-7 h-7"
        >
          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
          <path d="M9 7m-4 0a4 4 0 1 0 8 0a4 4 0 1 0 -8 0" />
          <path d="M3 21v-2a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4v2" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
          <path d="M21 21v-2a4 4 0 0 0 -3 -3.85" />
        </svg>
      </router-link>
      <router-link
        to="/more"
        class="relative flex-1 flex items-end justify-center pb-3"
        :class="isNavActive('/more') ? 'text-primary' : 'text-base-content'"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="icon icon-tabler icons-tabler-outline icon-tabler-dots w-7 h-7"
        >
          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
          <path d="M5 12m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0" />
          <path d="M12 12m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0" />
          <path d="M19 12m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0" />
        </svg>
      </router-link>
    </div>
  </nav>
</template>

<style scoped>
.recording-bar {
  width: 3px;
  height: 8px;
  border-radius: 999px;
  background: #ef4444;
  animation: recording-pulse 0.9s ease-in-out infinite;
}

.recording-bar-delay-1 {
  animation-delay: 0.1s;
}

.recording-bar-delay-2 {
  animation-delay: 0.2s;
}

.recording-bar-delay-3 {
  animation-delay: 0.3s;
}

.quick-create-slide-enter-active,
.quick-create-slide-leave-active {
  transition:
    opacity 220ms ease,
    transform 220ms ease,
    max-height 220ms ease;
}

.quick-create-slide-enter-from,
.quick-create-slide-leave-to {
  opacity: 0;
  transform: translateY(16px);
  max-height: 0;
}

.quick-create-slide-enter-to,
.quick-create-slide-leave-from {
  opacity: 1;
  transform: translateY(0);
  max-height: 16rem;
}

@keyframes recording-pulse {
  0%,
  100% {
    transform: scaleY(0.5);
    opacity: 0.7;
  }
  50% {
    transform: scaleY(1.8);
    opacity: 1;
  }
}

.transcribing-spinner {
  width: 12px;
  height: 12px;
  border-radius: 999px;
  border: 2px solid color-mix(in oklab, currentColor 30%, transparent);
  border-top-color: currentColor;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.no-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.no-scrollbar::-webkit-scrollbar {
  display: none;
}

.composer-input {
  position: relative;
}

.composer-input.composer-empty::before {
  content: attr(placeholder);
  color: rgb(100 116 139 / 0.7);
  position: absolute;
  left: 1rem;
  top: 0.5rem;
  pointer-events: none;
}
</style>
